import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { supabase } from '../../lib/supabase';
import { invoiceService } from '../../services/invoiceService';

const STATUS_CONFIG = {
  success: { label: 'Başarılı', bg: 'bg-green-100 text-green-700 border-green-200', dot: 'bg-green-500' },
  error: { label: 'Hata', bg: 'bg-red-100 text-red-700 border-red-200', dot: 'bg-red-500' },
  skipped: { label: 'Atlandı', bg: 'bg-yellow-100 text-yellow-700 border-yellow-200', dot: 'bg-yellow-500' },
};

const StatusBadge = ({ status }) => {
  const cfg = STATUS_CONFIG?.[status] || STATUS_CONFIG?.skipped;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border ${cfg?.bg}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${cfg?.dot}`} />
      {cfg?.label}
    </span>
  );
};

const SummaryCard = ({ title, value, icon, colorClass, subtitle }) => (
  <div className="bg-card rounded-xl border border-border p-5 flex items-center gap-4">
    <div className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 ${colorClass}`}>
      <Icon name={icon} size={20} />
    </div>
    <div>
      <p className="text-xs text-muted-foreground">{title}</p>
      <p className="text-2xl font-bold text-foreground">{value}</p>
      {subtitle && <p className="text-xs text-muted-foreground mt-0.5">{subtitle}</p>}
    </div>
  </div>
);

const InvoiceDetailModal = ({ log, onClose }) => {
  if (!log) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-card rounded-2xl border border-border w-full max-w-xl max-h-[90vh] overflow-y-auto shadow-xl">
        <div className="flex items-center justify-between p-5 border-b border-border">
          <div>
            <h2 className="text-base font-semibold text-foreground">Fatura Detayı</h2>
            <p className="text-xs text-muted-foreground mt-0.5">{log?.customer_name || '-'}</p>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-muted transition-colors">
            <Icon name="X" size={16} />
          </button>
        </div>
        <div className="p-5 space-y-4">
          <div className="flex items-center gap-3 flex-wrap">
            <StatusBadge status={log?.status} />
            {log?.is_retry && (
              <span className="px-2.5 py-1 rounded-full text-xs font-semibold border bg-blue-50 text-blue-700 border-blue-200">
                Yeniden Deneme #{log?.retry_count || 1}
              </span>
            )}
            <span className="text-xs text-muted-foreground">
              {log?.created_at ? new Date(log.created_at)?.toLocaleString('tr-TR') : '-'}
            </span>
          </div>

          <div className="bg-muted/40 rounded-xl p-4 grid grid-cols-2 gap-3 text-sm">
            <div>
              <p className="text-muted-foreground text-xs">Müşteri</p>
              <p className="font-medium text-foreground">{log?.customer_name || '-'}</p>
            </div>
            <div>
              <p className="text-muted-foreground text-xs">VKN / TCKN</p>
              <p className="font-medium text-foreground">{log?.customer_vkn_tckn || '-'}</p>
            </div>
            <div>
              <p className="text-muted-foreground text-xs">Belge Tipi</p>
              <p className="font-medium text-foreground">{log?.e_document_type || '-'}</p>
            </div>
            <div>
              <p className="text-muted-foreground text-xs">Tutar</p>
              <p className="font-medium text-foreground">
                {log?.total_amount != null ? `₺${parseFloat(log?.total_amount)?.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}` : '-'}
              </p>
            </div>
            {log?.doc_no && (
              <div>
                <p className="text-muted-foreground text-xs">Belge No</p>
                <p className="font-medium text-foreground">{log?.doc_no}</p>
              </div>
            )}
            {log?.ettn && (
              <div className="col-span-2">
                <p className="text-muted-foreground text-xs">ETTN</p>
                <p className="font-medium text-foreground break-all text-xs">{log?.ettn}</p>
              </div>
            )}
          </div>

          {log?.status === 'error' && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-sm">
              <p className="font-semibold text-red-700 mb-1">Hata Bilgisi</p>
              {log?.error_stage && <p className="text-red-600 text-xs mb-1">Aşama: {log?.error_stage}</p>}
              <p className="text-red-700 break-words">{log?.error_message || '-'}</p>
            </div>
          )}

          {log?.mysoft_response && (
            <div>
              <p className="text-xs font-semibold text-foreground mb-1">Mysoft Yanıtı</p>
              <pre className="bg-muted rounded-lg p-3 text-xs text-foreground overflow-x-auto whitespace-pre-wrap break-all">
                {JSON.stringify(log?.mysoft_response, null, 2)}
              </pre>
            </div>
          )}
        </div>
        <div className="px-5 pb-5">
          <Button variant="outline" onClick={onClose} className="w-full">Kapat</Button>
        </div>
      </div>
    </div>
  );
};

const EventInvoiceComplianceDashboard = ({ embedded = false }) => {
  const navigate = useNavigate();
  const [eventGroups, setEventGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedEvents, setExpandedEvents] = useState({});
  const [selectedLog, setSelectedLog] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [globalStats, setGlobalStats] = useState({ total: 0, success: 0, error: 0, skipped: 0, totalAmount: 0 });

  // Email send state
  const [sendingEmailId, setSendingEmailId] = useState(null);
  const [emailModalLog, setEmailModalLog] = useState(null);
  const [emailInput, setEmailInput] = useState('');
  const [toast, setToast] = useState(null);

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  };

  const handleSendEmail = async (log) => {
    if (!emailInput?.trim()) return;
    setSendingEmailId(log?.id);
    try {
      console.log('[handleSendEmail] Sending email for log:', log?.id, 'to:', emailInput?.trim());
      const result = await invoiceService?.sendInvoiceEmail(log, emailInput?.trim());
      console.log('[handleSendEmail] Result:', result);
      if (result?.success) {
        showToast(`E-fatura ${log?.customer_name || ''} adresine başarıyla gönderildi`, 'success');
        setEmailModalLog(null);
        setEmailInput('');
        loadData();
      } else {
        // success:false but no throw — still surface the error
        const errMsg = result?.error || 'Bilinmeyen hata';
        console.error('[handleSendEmail] Backend returned success:false —', errMsg);
        showToast(`E-posta gönderilemedi: ${errMsg}`, 'error');
        // Do NOT close the modal so admin can see the error
      }
    } catch (err) {
      console.error('EMAIL DISPATCH CRASH:', err);
      const errMsg = err?.message || 'Bilinmeyen bir hata oluştu';
      try {
        showToast(`Hata: ${errMsg}`, 'error');
      } catch (toastErr) {
        console.error('[handleSendEmail] Toast failed too:', toastErr);
      }
      // Native alert fallback in case toast library is failing
      alert('Sistem Hatası: ' + errMsg + '\n\nDetaylar için tarayıcı konsolunu kontrol edin (F12).');
      // Do NOT close the modal — keep it open so admin can see what failed
    } finally {
      setSendingEmailId(null);
    }
  };

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError('');

      // Fetch ONLY event invoice logs (source = EVENT_RESERVATION or event_id is not null)
      const { data: logs, error: logsError } = await supabase
        ?.from('invoice_logs')
        ?.select('*')
        ?.or('source.eq.EVENT_RESERVATION,invoice_type.eq.EVENT_RESERVATION')
        ?.order('created_at', { ascending: false });

      if (logsError) throw logsError;

      if (!logs?.length) {
        setEventGroups([]);
        setGlobalStats({ total: 0, success: 0, error: 0, skipped: 0, totalAmount: 0 });
        return;
      }

      // Collect all reservation_ids (event invoice logs store registration ID in reservation_id, not order_id)
      const reservationIds = [...new Set(logs.map(l => l.reservation_id || l.order_id).filter(Boolean))];

      // Try to find matching event_registrations
      let registrationMap = {};
      let eventMap = {};

      if (reservationIds?.length > 0) {
        const { data: registrations } = await supabase?.from('event_registrations')?.select('id, event_id, participant_name, participant_email')?.in('id', reservationIds);

        if (registrations?.length) {
          registrations?.forEach(r => { registrationMap[r.id] = r; });

          const eventIds = [...new Set(registrations.map(r => r.event_id).filter(Boolean))];
          if (eventIds?.length > 0) {
            const { data: events } = await supabase?.from('events')?.select('id, title, event_date, location')?.in('id', eventIds);

            if (events?.length) {
              events?.forEach(e => { eventMap[e.id] = e; });
            }
          }
        }
      }

      // Also build eventMap from event_id directly on logs
      const directEventIds = [...new Set(logs.map(l => l.event_id).filter(Boolean))];
      if (directEventIds?.length > 0) {
        const missingIds = directEventIds?.filter(id => !eventMap?.[id]);
        if (missingIds?.length > 0) {
          const { data: directEvents } = await supabase?.from('events')?.select('id, title, event_date, location')?.in('id', missingIds);
          if (directEvents?.length) {
            directEvents?.forEach(e => { eventMap[e.id] = e; });
          }
        }
      }

      // Group logs by event — NO ungrouped bucket
      const groups = {};

      logs?.forEach(log => {
        const reg = registrationMap?.[log?.reservation_id] || registrationMap?.[log?.order_id];
        const eventId = log?.event_id || reg?.event_id;
        const event = eventId ? eventMap?.[eventId] : null;

        // Use event_id as group key; fall back to a placeholder if event not found yet
        const groupKey = eventId || `unknown_${log?.id}`;
        if (!groups?.[groupKey]) {
          groups[groupKey] = {
            eventId: eventId || null,
            eventTitle: event?.title || log?.invoice_payload?.eventTitle || (eventId ? `Etkinlik #${eventId?.slice(0, 8)}` : 'Bilinmeyen Etkinlik'),
            eventDate: event?.event_date || null,
            eventLocation: event?.location || null,
            logs: [],
            stats: { total: 0, success: 0, error: 0, skipped: 0, totalAmount: 0 },
          };
        }

        groups?.[groupKey]?.logs?.push({
          ...log,
          _registration: reg || null,
          customer_email: log?.customer_email || reg?.participant_email || null,
          pdf_url: log?.pdf_url || null,
        });
        groups[groupKey].stats.total++;
        if (log?.status === 'success') {
          groups[groupKey].stats.success++;
          groups[groupKey].stats.totalAmount += parseFloat(log?.total_amount || 0);
        } else if (log?.status === 'error') {
          groups[groupKey].stats.error++;
        } else if (log?.status === 'skipped') {
          groups[groupKey].stats.skipped++;
        }
      });

      // Sort groups by event date desc
      const sortedGroups = Object.values(groups)?.sort((a, b) => {
        if (a?.eventDate && b?.eventDate) return new Date(b.eventDate) - new Date(a.eventDate);
        if (a?.eventDate) return -1;
        if (b?.eventDate) return 1;
        return 0;
      });

      // Compute global stats
      const gStats = { total: logs?.length, success: 0, error: 0, skipped: 0, totalAmount: 0 };
      logs?.forEach(l => {
        if (l?.status === 'success') { gStats.success++; gStats.totalAmount += parseFloat(l?.total_amount || 0); }
        else if (l?.status === 'error') gStats.error++;
        else if (l?.status === 'skipped') gStats.skipped++;
      });

      setGlobalStats(gStats);
      setEventGroups(sortedGroups);
    } catch (err) {
      console.error('EventInvoiceCompliance load error:', err);
      setError(err?.message || 'Veriler yüklenemedi');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const userRole = localStorage.getItem('userRole');
    if (userRole !== 'admin') {
      navigate('/event-discovery');
      return;
    }
    loadData();
  }, [navigate, loadData]);

  const toggleEvent = (key) => {
    setExpandedEvents(prev => ({ ...prev, [key]: !prev?.[key] }));
  };

  const filteredGroups = eventGroups?.map(group => {
    const filteredLogs = group?.logs?.filter(log => {
      const matchesStatus = statusFilter === 'all' || log?.status === statusFilter;
      const q = searchQuery?.toLowerCase();
      const matchesSearch = !q ||
        log?.customer_name?.toLowerCase()?.includes(q) ||
        log?.customer_vkn_tckn?.toLowerCase()?.includes(q) ||
        log?.doc_no?.toLowerCase()?.includes(q) ||
        log?.order_number?.toLowerCase()?.includes(q) ||
        group?.eventTitle?.toLowerCase()?.includes(q);
      return matchesStatus && matchesSearch;
    });
    return { ...group, logs: filteredLogs };
  })?.filter(g => g?.logs?.length > 0);

  const successRate = globalStats?.total > 0
    ? Math.round((globalStats?.success / globalStats?.total) * 100)
    : 0;

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="w-10 h-10 border-4 border-primary/20 border-t-primary rounded-full animate-spin mx-auto mb-3" />
          <p className="text-muted-foreground text-sm">Fatura kayıtları yükleniyor...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={embedded ? '' : 'min-h-screen bg-background'}>
      {!embedded && <Header />}
      <div className={embedded ? '' : 'main-content'}>
        <div className={embedded ? '' : 'max-w-[1400px] mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8'}>

          {/* Toast */}
          {toast && (
            <div className={`fixed top-6 right-6 z-[100] flex items-center gap-3 px-4 py-3 rounded-xl shadow-lg border text-sm font-medium transition-all ${toast?.type === 'success' ? 'bg-green-50 border-green-200 text-green-800' : 'bg-red-50 border-red-200 text-red-800'}`}>
              <Icon name={toast?.type === 'success' ? 'CheckCircle' : 'XCircle'} size={16} />
              {toast?.message}
            </div>
          )}

          {/* Page Header */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
            <div className="flex items-center gap-3">
              {!embedded && (
                <button
                  onClick={() => navigate('/admin-dashboard')}
                  className="p-2 rounded-lg hover:bg-muted transition-colors text-muted-foreground"
                >
                  <Icon name="ArrowLeft" size={18} />
                </button>
              )}
              <div>
                <h1 className="text-xl md:text-2xl font-bold text-foreground">Etkinlik Fatura Uyumluluk Paneli</h1>
                <p className="text-xs text-muted-foreground mt-0.5">Etkinlik bazında e-fatura gönderim geçmişi ve durum takibi</p>
              </div>
            </div>
            <Button
              variant="outline"
              iconName="RefreshCw"
              iconPosition="left"
              onClick={loadData}
            >
              Yenile
            </Button>
          </div>

          {error && (
            <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4 mb-6 flex items-center gap-3">
              <Icon name="AlertCircle" size={16} className="text-destructive flex-shrink-0" />
              <p className="text-destructive text-sm">{error}</p>
            </div>
          )}

          {/* Global Stats */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <SummaryCard
              title="Toplam Fatura"
              value={globalStats?.total}
              icon="FileText"
              colorClass="bg-primary/10 text-primary"
              subtitle=""
            />
            <SummaryCard
              title="Başarılı"
              value={globalStats?.success}
              icon="CheckCircle"
              colorClass="bg-green-100 text-green-700"
              subtitle={`%${successRate} başarı oranı`}
            />
            <SummaryCard
              title="Hatalı"
              value={globalStats?.error}
              icon="XCircle"
              colorClass="bg-red-100 text-red-700"
              subtitle={globalStats?.skipped > 0 ? `${globalStats?.skipped} atlandı` : ""}
            />
            <SummaryCard
              title="Toplam Tutar"
              value={`₺${globalStats?.totalAmount?.toLocaleString('tr-TR', { minimumFractionDigits: 0 })}`}
              icon="TrendingUp"
              colorClass="bg-blue-100 text-blue-700"
              subtitle="Başarılı faturalar"
            />
          </div>

          {/* Filters */}
          <div className="flex flex-col sm:flex-row gap-3 mb-6">
            <div className="relative flex-1">
              <Icon name="Search" size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input
                type="text"
                placeholder="Müşteri adı, VKN/TCKN, belge no, etkinlik..."
                value={searchQuery}
                onChange={e => setSearchQuery(e?.target?.value)}
                className="w-full pl-9 pr-4 py-2.5 text-sm bg-card border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 text-foreground placeholder:text-muted-foreground"
              />
            </div>
            <div className="flex gap-2">
              {['all', 'success', 'error', 'skipped']?.map(s => (
                <button
                  key={s}
                  onClick={() => setStatusFilter(s)}
                  className={`px-3 py-2 rounded-lg text-xs font-medium transition-colors whitespace-nowrap ${
                    statusFilter === s
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-card border border-border text-foreground hover:bg-muted'
                  }`}
                >
                  {s === 'all' ? 'Tümü' : STATUS_CONFIG?.[s]?.label}
                </button>
              ))}
            </div>
          </div>

          {/* Event Groups */}
          {filteredGroups?.length === 0 ? (
            <div className="bg-card rounded-xl border border-border p-12 text-center">
              <Icon name="Receipt" size={40} className="text-muted-foreground mx-auto mb-3" />
              <p className="text-foreground font-medium mb-1">Fatura kaydı bulunamadı</p>
              <p className="text-muted-foreground text-sm">Arama kriterlerinizi değiştirmeyi deneyin.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {filteredGroups?.map((group, idx) => {
                const groupKey = group?.eventId || '__ungrouped__';
                const isExpanded = expandedEvents?.[groupKey] !== false; // default expanded
                const hasErrors = group?.stats?.error > 0;

                return (
                  <div key={groupKey} className="bg-card rounded-xl border border-border overflow-hidden">
                    {/* Event Header */}
                    <button
                      onClick={() => toggleEvent(groupKey)}
                      className="w-full flex items-center justify-between p-4 md:p-5 hover:bg-muted/30 transition-colors text-left"
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${group?.eventId ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground'}`}>
                          <Icon name={group?.eventId ? 'Calendar' : 'ShoppingBag'} size={16} />
                        </div>
                        <div className="min-w-0">
                          <p className="font-semibold text-foreground text-sm truncate">{group?.eventTitle}</p>
                          <div className="flex items-center gap-3 mt-0.5 flex-wrap">
                            {group?.eventDate && (
                              <span className="text-xs text-muted-foreground flex items-center gap-1">
                                <Icon name="CalendarDays" size={11} />
                                {new Date(group.eventDate)?.toLocaleDateString('tr-TR')}
                              </span>
                            )}
                            {group?.eventLocation && (
                              <span className="text-xs text-muted-foreground flex items-center gap-1">
                                <Icon name="MapPin" size={11} />
                                {group?.eventLocation}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 flex-shrink-0 ml-3">
                        {/* Mini stats */}
                        <div className="hidden sm:flex items-center gap-2">
                          <span className="flex items-center gap-1 text-xs text-green-700 bg-green-50 border border-green-200 px-2 py-0.5 rounded-full">
                            <Icon name="Check" size={10} /> {group?.stats?.success}
                          </span>
                          {group?.stats?.error > 0 && (
                            <span className="flex items-center gap-1 text-xs text-red-700 bg-red-50 border border-red-200 px-2 py-0.5 rounded-full">
                              <Icon name="X" size={10} /> {group?.stats?.error}
                            </span>
                          )}
                          <span className="text-xs text-muted-foreground">{group?.stats?.total} kayıt</span>
                        </div>
                        {hasErrors && (
                          <span className="w-2 h-2 rounded-full bg-red-500 flex-shrink-0" title="Hatalı fatura var" />
                        )}
                        <Icon name={isExpanded ? 'ChevronUp' : 'ChevronDown'} size={16} className="text-muted-foreground" />
                      </div>
                    </button>
                    {/* Invoice Table */}
                    {isExpanded && (
                      <div className="border-t border-border">
                        {/* Mobile stats bar */}
                        <div className="sm:hidden flex items-center gap-3 px-4 py-2 bg-muted/20 border-b border-border">
                          <span className="text-xs text-green-700">{group?.stats?.success} başarılı</span>
                          {group?.stats?.error > 0 && <span className="text-xs text-red-700">{group?.stats?.error} hatalı</span>}
                          <span className="text-xs text-muted-foreground">{group?.stats?.total} toplam</span>
                        </div>

                        <div className="overflow-x-auto">
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="border-b border-border bg-muted/20">
                                <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground whitespace-nowrap">Durum</th>
                                <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground whitespace-nowrap">Müşteri</th>
                                <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground whitespace-nowrap hidden md:table-cell">VKN / TCKN</th>
                                <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground whitespace-nowrap hidden lg:table-cell">Belge No</th>
                                <th className="text-right px-4 py-3 text-xs font-semibold text-muted-foreground whitespace-nowrap hidden sm:table-cell">Tutar</th>
                                <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground whitespace-nowrap">Tarih</th>
                                <th className="text-center px-4 py-3 text-xs font-semibold text-muted-foreground whitespace-nowrap">Detay</th>
                              </tr>
                            </thead>
                            <tbody>
                              {group?.logs?.map((log, logIdx) => {
                                const isSendingEmail = sendingEmailId === log?.id;
                                return (
                                  <tr
                                    key={log?.id}
                                    className={`border-b border-border/50 hover:bg-muted/20 transition-colors ${logIdx % 2 === 0 ? '' : 'bg-muted/5'}`}
                                  >
                                    <td className="px-4 py-3">
                                      <div className="flex flex-col gap-1">
                                        <StatusBadge status={log?.status} />
                                        {log?.email_sent && (
                                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-teal-50 text-teal-700 border border-teal-200 w-fit">
                                            <Icon name="Mail" size={10} />
                                            Email Sent
                                          </span>
                                        )}
                                      </div>
                                    </td>
                                    <td className="px-4 py-3">
                                      <p className="font-medium text-foreground text-xs">{log?.customer_name || '-'}</p>
                                      {log?.status === 'error' && log?.error_message && (
                                        <p className="text-xs text-red-600 mt-0.5 truncate max-w-[180px]" title={log?.error_message}>
                                          {log?.error_message}
                                        </p>
                                      )}
                                    </td>
                                    <td className="px-4 py-3 hidden md:table-cell">
                                      <span className="text-xs text-muted-foreground font-mono">{log?.customer_vkn_tckn || '-'}</span>
                                    </td>
                                    <td className="px-4 py-3 hidden lg:table-cell">
                                      <span className="text-xs text-muted-foreground font-mono">{log?.doc_no || '-'}</span>
                                    </td>
                                    <td className="px-4 py-3 text-right hidden sm:table-cell">
                                      <span className="text-xs font-medium text-foreground">
                                        {log?.total_amount != null
                                          ? `₺${parseFloat(log?.total_amount)?.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}`
                                          : '-'}
                                      </span>
                                    </td>
                                    <td className="px-4 py-3">
                                      <div>
                                        <p className="text-xs text-foreground whitespace-nowrap">
                                          {log?.created_at ? new Date(log.created_at)?.toLocaleDateString('tr-TR') : '-'}
                                        </p>
                                        <p className="text-xs text-muted-foreground whitespace-nowrap">
                                          {log?.created_at ? new Date(log.created_at)?.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' }) : ''}
                                        </p>
                                      </div>
                                    </td>
                                    <td className="px-4 py-3 text-center">
                                      <div className="flex items-center justify-center gap-1">
                                        <button
                                          onClick={() => setSelectedLog(log)}
                                          className="p-1.5 rounded-lg hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
                                          title="Fatura Detayı"
                                        >
                                          <Icon name="Eye" size={14} />
                                        </button>
                                        {log?.pdf_url ? (
                                          <button
                                            onClick={() => window.open(log?.pdf_url, '_blank', 'noopener,noreferrer')}
                                            className="p-1.5 rounded-lg hover:bg-green-50 transition-colors text-green-600 hover:text-green-700"
                                            title="PDF Görüntüle"
                                          >
                                            <Icon name="FileText" size={14} />
                                          </button>
                                        ) : (
                                          <button
                                            disabled
                                            className="p-1.5 rounded-lg text-muted-foreground/30 cursor-not-allowed"
                                            title="PDF mevcut değil"
                                          >
                                            <Icon name="FileText" size={14} />
                                          </button>
                                        )}
                                        {log?.status === 'success' && (
                                          <button
                                            onClick={() => { setEmailModalLog(log); setEmailInput(log?.customer_email || ''); }}
                                            disabled={isSendingEmail}
                                            className={`p-1.5 rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${log?.email_sent ? 'text-teal-600 hover:bg-teal-50 hover:text-teal-700' : 'text-blue-500 hover:bg-blue-50 hover:text-blue-700'}`}
                                            title={log?.email_sent ? 'Tekrar Gönder' : 'Müşteriye Gönder'}
                                          >
                                            <Icon name={isSendingEmail ? 'Loader' : 'Send'} size={14} className={isSendingEmail ? 'animate-spin' : ''} />
                                          </button>
                                        )}
                                      </div>
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>

                        {/* Event footer summary */}
                        <div className="flex items-center justify-between px-4 py-3 bg-muted/10 border-t border-border/50">
                          <span className="text-xs text-muted-foreground">{group?.stats?.total} fatura kaydı</span>
                          <span className="text-xs font-semibold text-foreground">
                            Toplam: ₺{group?.stats?.totalAmount?.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
      {selectedLog && (
        <InvoiceDetailModal log={selectedLog} onClose={() => setSelectedLog(null)} />
      )}
      {emailModalLog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-card rounded-2xl border border-border w-full max-w-md shadow-xl">
            <div className="flex items-center justify-between p-6 border-b border-border">
              <div>
                <h2 className="text-base font-semibold text-foreground">Müşteriye Gönder</h2>
                <p className="text-xs text-muted-foreground mt-0.5">{emailModalLog?.customer_name} — {emailModalLog?.doc_no || emailModalLog?.ettn?.slice(0, 12)}</p>
              </div>
              <button onClick={() => { setEmailModalLog(null); setEmailInput(''); }} className="p-2 rounded-lg hover:bg-muted transition-colors">
                <Icon name="X" size={16} />
              </button>
            </div>
            <div className="p-6 space-y-4">
              {emailModalLog?.email_sent && (
                <div className="flex items-center gap-2 bg-teal-50 border border-teal-200 rounded-lg px-3 py-2 text-sm text-teal-700">
                  <Icon name="CheckCircle" size={14} />
                  Bu fatura daha önce gönderildi. Tekrar gönderebilirsiniz.
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">Müşteri E-posta Adresi</label>
                <input
                  type="email"
                  value={emailInput}
                  onChange={(e) => setEmailInput(e?.target?.value)}
                  placeholder="ornek@email.com"
                  className="w-full px-3 py-2.5 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 text-foreground"
                />
              </div>
              <p className="text-xs text-muted-foreground">Mysoft'tan imzalı PDF çekilerek bu adrese gönderilecektir.</p>
            </div>
            <div className="px-6 pb-6 flex gap-3">
              <Button variant="outline" onClick={() => { setEmailModalLog(null); setEmailInput(''); }} className="flex-1">İptal</Button>
              <Button
                variant="default"
                iconName={sendingEmailId === emailModalLog?.id ? 'Loader' : 'Send'}
                iconPosition="left"
                disabled={!emailInput?.trim() || sendingEmailId === emailModalLog?.id}
                onClick={() => handleSendEmail(emailModalLog)}
                className="flex-1"
              >
                {sendingEmailId === emailModalLog?.id ? 'Gönderiliyor...' : 'Gönder'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EventInvoiceComplianceDashboard;
